import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Plus, Receipt, Car, UtensilsCrossed, FileText, Search, X, Paperclip, Camera, Download, FileDown, Archive, Pencil,
  Trash2, Info,
} from 'lucide-react';
import { toast } from 'sonner';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useProject } from '@/lib/ProjectContext';
import { useModules, hasModule } from '@/lib/modules';
import { getCurrentUser } from '@/lib/auth';
import { expensesAPI, invoicesAPI, fmtMoney, fmtDay, todayISO, addDaysISO, openBlob, PERIODS, periodParams, thisMonthISO } from '@/lib/financeAPI';

const red = 'bg-[#A62B1F] hover:bg-[#8C2318] text-white';
const card = 'rounded-xl border border-[#E3DDD1] bg-white';
const serif = { fontFamily: 'Fraunces, Georgia, serif' };
const sel = 'w-full h-10 rounded-md border border-input px-2 text-sm bg-white';
const err = (e, fallback) => toast.error(e?.response?.data?.detail || fallback);
const isAdmin = () => ['owner', 'admin'].includes(getCurrentUser()?.role) || getCurrentUser()?.is_super_admin;

const KIND = {
  expense: { label: 'Receipt', icon: Receipt, add: 'Receipt' },
  mileage: { label: 'Mileage', icon: Car, add: 'Mileage' },
  per_diem: { label: 'Per diem', icon: UtensilsCrossed, add: 'Per diem' },
  bill: { label: 'Bill to pay', icon: FileText, add: 'Bill' },
};
const MEALS = [['breakfast', 'Breakfast'], ['lunch', 'Lunch'], ['dinner', 'Dinner'], ['incidentals', 'Incidentals']];

const Tile = ({ label, value, note }) => (
  <div className={`${card} p-4`}>
    <div className="text-[11px] uppercase tracking-wider text-[#5C6670] font-semibold">{label}</div>
    <div className="text-xl font-semibold mt-1 text-[#1E2A33]">{value}</div>
    {note && <div className="text-xs text-[#5C6670] mt-0.5">{note}</div>}
  </div>
);

const Chip = ({ on, onClick, children }) => (
  <button onClick={onClick} className={`text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap ${on ? 'bg-[#1E2A33] text-white' : 'bg-[#F3EFE7] text-[#5C6670]'}`}>{children}</button>
);

const Field = ({ label, children, className = '' }) => <div className={className}><span className="text-[11px] text-[#5C6670]">{label}</span>{children}</div>;

// The individual or company an expense is recorded for: their logo, or initials
const EntityBadge = ({ entity, size = 24 }) => {
  if (!entity) return null;
  const initials = (entity.name || '?').split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase();
  return entity.logo_url ? (
    <img src={entity.logo_url} alt="" title={entity.name} style={{ height: size, maxWidth: size * 3 }} className="object-contain rounded shrink-0" />
  ) : (
    <span title={entity.name} style={{ width: size, height: size, fontSize: size * 0.4 }}
      className={`inline-flex items-center justify-center font-bold shrink-0 ${entity.type === 'individual' ? 'rounded-full bg-[#1E2A33] text-white' : 'rounded-md bg-[#A62B1F] text-white'}`}>{initials}</span>
  );
};

const entityById = (settings, id) => (settings?.entities || []).find((x) => x.id === id);

const EntityPicker = ({ settings, value, onChange }) => (
  <div className="flex gap-1.5 flex-wrap">
    {(settings?.entities || []).map((x) => (
      <button key={x.id} type="button" onClick={() => onChange(x.id)}
        className={`flex items-center gap-2 pl-1.5 pr-3 py-1 rounded-lg border text-sm ${value === x.id ? 'border-[#1E2A33] bg-[#F3EFE7] font-semibold text-[#1E2A33]' : 'border-[#E3DDD1] text-[#5C6670]'}`}>
        <EntityBadge entity={x} size={22} /><span>{x.name}</span>
        <span className="text-[10px] uppercase tracking-wide text-[#94A3B8]">{x.type === 'individual' ? 'Individual' : 'Company'}</span>
      </button>
    ))}
  </div>
);

const PeriodPicker = ({ value, onChange }) => (
  <div className="flex gap-2 flex-wrap items-center">
    <select className="h-9 rounded-md border border-input px-2 text-sm bg-white" value={value.period} onChange={(e) => onChange({ ...value, period: e.target.value, month: value.month || thisMonthISO() })}>
      {PERIODS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
      <option value="all">All dates</option>
    </select>
    {value.period === 'month' && (
      <Input type="month" className="h-9 w-44" value={value.month || thisMonthISO()} max={thisMonthISO()} onChange={(e) => onChange({ ...value, month: e.target.value })} />
    )}
    {value.period === 'custom' && (
      <>
        <Input type="date" className="h-9 w-40" value={value.from_ || ''} onChange={(e) => onChange({ ...value, from_: e.target.value })} />
        <span className="text-xs text-[#5C6670]">to</span>
        <Input type="date" className="h-9 w-40" value={value.to || ''} onChange={(e) => onChange({ ...value, to: e.target.value })} />
      </>
    )}
  </div>
);


// ── Add / edit ───────────────────────────────────────────────────────────────
const blank = (kind, settings) => ({
  kind, scope: 'business', date: todayISO(), vendor: '', description: '', reason: '', category: '', amount: '', tax_amount: '',
  payment_method: kind === 'bill' ? 'Not paid yet' : (settings?.payment_methods?.[0] || ''), project_id: '', contact_id: '', contact_name: '',
  billable: false, reimbursable: false, entity_id: settings?.default_entity_id || '',
  mileage: { from: '', to: '', km: '', round_trip: false, region: settings?.default_region || 'Canada', vehicle: '' },
  per_diem: { days: 1, meals: { breakfast: false, lunch: true, dinner: false, incidentals: false }, region: settings?.default_region || 'Canada', location: '' },
  bill: { number: '', due_date: addDaysISO(todayISO(), 30), status: 'unpaid', paid_date: '' },
  receipts: [],
});

const ExpenseDialog = ({ open, onOpenChange, initial, settings, onSaved }) => {
  const { projects = [] } = useProject() || {};
  const mods = useModules();
  const [f, setF] = useState(null);
  const [files, setFiles] = useState([]);
  const [calc, setCalc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [contactQ, setContactQ] = useState('');
  const [contactHits, setContactHits] = useState([]);
  const fileRef = useRef(null);
  const camRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const b = blank(initial?.kind || 'expense', settings);
    setF(initial?.id ? { ...b, ...initial, project_id: initial.project_id || '', contact_id: initial.contact_id || '', amount: initial.amount ?? '', tax_amount: initial.tax_amount ?? '',
      mileage: { ...b.mileage, ...(initial.mileage || {}) }, per_diem: { ...b.per_diem, ...(initial.per_diem || {}) }, bill: { ...b.bill, ...(initial.bill || {}) } } : b);
    setFiles([]); setCalc(null); setContactQ('');
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const priced = f && (f.kind === 'mileage' || f.kind === 'per_diem');
  // live price for mileage and per diems, from the rate in force on the date
  useEffect(() => {
    if (!open || !priced) return undefined;
    const t = setTimeout(() => {
      const body = { kind: f.kind, date: f.date, mileage: f.kind === 'mileage' ? { ...f.mileage, km: Number(f.mileage.km || 0) } : undefined,
        per_diem: f.kind === 'per_diem' ? { ...f.per_diem, days: Number(f.per_diem.days || 0) } : undefined };
      if (f.kind === 'mileage' && !Number(f.mileage.km)) { setCalc(null); return; }
      expensesAPI.calculate(body).then((r) => setCalc({ ok: true, ...r.data })).catch((e) => setCalc({ ok: false, error: e?.response?.data?.detail }));
    }, 300);
    return () => clearTimeout(t);
  }, [open, f?.kind, f?.date, JSON.stringify(f?.mileage), JSON.stringify(f?.per_diem)]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!contactQ || !hasModule(mods, 'invoices')) { setContactHits([]); return undefined; }
    const t = setTimeout(() => invoicesAPI.clients(contactQ).then((r) => setContactHits(r.data)).catch(() => {}), 200);
    return () => clearTimeout(t);
  }, [contactQ]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!f) return null;
  const set = (patch) => setF({ ...f, ...patch });
  const setM = (patch) => setF({ ...f, mileage: { ...f.mileage, ...patch } });
  const setP = (patch) => setF({ ...f, per_diem: { ...f.per_diem, ...patch } });
  const setB = (patch) => setF({ ...f, bill: { ...f.bill, ...patch } });
  const editing = !!f.id;
  const taxFrom = (rate) => { const a = Number(f.amount || 0); set({ tax_amount: (Math.round((a * rate / (100 + rate)) * 100) / 100).toFixed(2) }); };

  const save = async () => {
    if ((f.kind === 'expense' || f.kind === 'bill') && !Number(f.amount)) return toast.error('Enter the amount');
    setBusy(true);
    const body = { kind: f.kind, scope: f.scope, date: f.date, vendor: f.vendor, description: f.description, reason: f.reason, category: f.category,
      payment_method: f.payment_method, project_id: f.project_id || '', contact_id: f.contact_id || '', billable: f.billable, reimbursable: f.reimbursable,
      entity_id: f.entity_id || undefined };
    if (f.kind === 'expense' || f.kind === 'bill') { body.amount = Number(f.amount || 0); body.tax_amount = Number(f.tax_amount || 0); }
    if (f.kind === 'mileage') body.mileage = { ...f.mileage, km: Number(f.mileage.km || 0) };
    if (f.kind === 'per_diem') body.per_diem = { ...f.per_diem, days: Number(f.per_diem.days || 0) };
    if (f.kind === 'bill') body.bill = { ...f.bill, due_date: f.bill.due_date || null, paid_date: f.bill.paid_date || null };
    try {
      let r = editing ? await expensesAPI.update(f.id, body) : await expensesAPI.create(body);
      if (files.length) r = await expensesAPI.uploadReceipts(r.data.id, files);
      toast.success(editing ? 'Saved' : `${KIND[f.kind].label} added`);
      onOpenChange(false); onSaved(r.data);
    } catch (e) { err(e, 'Could not save'); } finally { setBusy(false); }
  };

  const Toggle = ({ k, label, hint }) => (
    <label className="flex items-start gap-2 text-sm cursor-pointer">
      <Switch checked={!!f[k]} onCheckedChange={(v) => set({ [k]: v })} className="mt-0.5" />
      <span><span className="text-[#1E2A33]">{label}</span>{hint && <span className="block text-[11px] text-[#5C6670]">{hint}</span>}</span>
    </label>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? `Edit ${KIND[f.kind].label.toLowerCase()}` : 'Add to expenses'}</DialogTitle>
          <DialogDescription>{f.kind === 'mileage' ? 'Priced from the per-km rate in force on the date of the trip.' : f.kind === 'per_diem' ? 'Meal allowances from the rate in force on the date.' : f.kind === 'bill' ? 'An invoice you need to pay, with its due date.' : 'Snap or attach the receipt; your accountant will want it.'}</DialogDescription>
        </DialogHeader>
        {!editing && (
          <div className="flex gap-1 flex-wrap mb-1">
            {Object.entries(KIND).map(([k, v]) => { const I = v.icon; return <Chip key={k} on={f.kind === k} onClick={() => set({ kind: k, category: '', payment_method: k === 'bill' ? 'Not paid yet' : (settings?.payment_methods?.[0] || '') })}><I className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />{v.label}</Chip>; })}
          </div>
        )}
        <div className="space-y-3">
          {(settings?.entities || []).length > 1 && (
            <Field label="Recorded for"><div className="mt-1"><EntityPicker settings={settings} value={f.entity_id} onChange={(id) => set({ entity_id: id })} /></div></Field>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <Field label={f.kind === 'bill' ? 'Bill date' : 'Date'}><Input type="date" value={f.date} onChange={(e) => set({ date: e.target.value })} /></Field>
            {(f.kind === 'expense' || f.kind === 'bill') && (
              <Field label="Business or personal" className="sm:col-span-2">
                <div className="flex gap-1 h-10 items-center">
                  <Chip on={f.scope === 'business'} onClick={() => set({ scope: 'business' })}>Business</Chip>
                  <Chip on={f.scope === 'personal'} onClick={() => set({ scope: 'personal', billable: false, reimbursable: false })}>Personal</Chip>
                </div>
              </Field>
            )}
          </div>

          {f.kind === 'mileage' && (
            <div className="rounded-lg border border-[#E3DDD1] p-3 space-y-2">
              <div className="grid sm:grid-cols-2 gap-2">
                <Field label="From"><Input value={f.mileage.from} onChange={(e) => setM({ from: e.target.value })} placeholder="Office, Toronto" /></Field>
                <Field label="To"><Input value={f.mileage.to} onChange={(e) => setM({ to: e.target.value })} placeholder="Client, Mississauga" /></Field>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 items-end">
                <Field label="Kilometres (one way)"><Input type="number" step="0.1" value={f.mileage.km} onChange={(e) => setM({ km: e.target.value })} /></Field>
                <label className="flex items-center gap-2 text-sm h-10"><Switch checked={f.mileage.round_trip} onCheckedChange={(v) => setM({ round_trip: v })} />Round trip</label>
                <Field label="Rate region"><select className={sel} value={f.mileage.region} onChange={(e) => setM({ region: e.target.value })}>{(settings?.regions || ['Canada']).map((r) => <option key={r}>{r}</option>)}</select></Field>
                <Field label="Vehicle (optional)"><Input value={f.mileage.vehicle} onChange={(e) => setM({ vehicle: e.target.value })} /></Field>
              </div>
            </div>
          )}

          {f.kind === 'per_diem' && (
            <div className="rounded-lg border border-[#E3DDD1] p-3 space-y-2">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                <Field label="Where" className="col-span-2 sm:col-span-1"><Input value={f.per_diem.location} onChange={(e) => setP({ location: e.target.value })} placeholder="Ottawa" /></Field>
                <Field label="Days"><Input type="number" step="0.5" min="0.5" value={f.per_diem.days} onChange={(e) => setP({ days: e.target.value })} /></Field>
                <Field label="Rate region"><select className={sel} value={f.per_diem.region} onChange={(e) => setP({ region: e.target.value })}>{(settings?.regions || ['Canada']).map((r) => <option key={r}>{r}</option>)}</select></Field>
              </div>
              <div className="flex gap-4 flex-wrap">
                {MEALS.map(([k, l]) => (
                  <label key={k} className="flex items-center gap-1.5 text-sm"><input type="checkbox" checked={!!f.per_diem.meals[k]} onChange={(e) => setP({ meals: { ...f.per_diem.meals, [k]: e.target.checked } })} />{l}
                    {calc?.per_diem?.rates?.[k] != null && <span className="text-[11px] text-[#94A3B8]">{fmtMoney(calc.per_diem.rates[k])}</span>}</label>
                ))}
              </div>
            </div>
          )}

          {priced && (
            <div className={`rounded-lg px-3 py-2 text-sm flex items-center justify-between ${calc?.ok === false ? 'bg-red-50 text-[#A62B1F]' : 'bg-[#F3EFE7]'}`}>
              {calc?.ok === false ? <span>{calc.error}</span> : calc?.ok ? (
                <>
                  <span className="text-[#5C6670]">{f.kind === 'mileage'
                    ? `${calc.mileage.distance} km at ${calc.mileage.rate_label}${calc.mileage.km_threshold ? ` (${Number(calc.mileage.prior_km_this_year || 0).toLocaleString()} km logged earlier this year)` : ''}`
                    : `${calc.per_diem.days} day${calc.per_diem.days === 1 ? '' : 's'} x ${fmtMoney(calc.per_diem.per_day)}`}
                    <span className="block text-[11px]">Rate from {fmtDay(calc.mileage?.rate_effective || calc.per_diem?.rate_effective)}</span></span>
                  <span className="text-lg font-semibold text-[#1E2A33]">{fmtMoney(calc.amount)}</span>
                </>
              ) : <span className="text-[#5C6670]">Enter the trip to see the amount</span>}
            </div>
          )}

          {(f.kind === 'expense' || f.kind === 'bill') && (
            <>
              <div className="grid sm:grid-cols-2 gap-2">
                <Field label={f.kind === 'bill' ? 'Who it\'s from' : 'Vendor'}><Input value={f.vendor} onChange={(e) => set({ vendor: e.target.value })} placeholder={f.kind === 'bill' ? 'Supplier' : 'Store, restaurant, airline…'} /></Field>
                <Field label="Category"><select className={sel} value={f.category} onChange={(e) => set({ category: e.target.value })}>
                  <option value="">Choose…</option>{(settings?.categories || []).map((c) => <option key={c}>{c}</option>)}</select></Field>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                <Field label="Total paid (with tax)"><Input type="number" step="0.01" value={f.amount} onChange={(e) => set({ amount: e.target.value })} /></Field>
                <Field label="Of which HST/GST">
                  <Input type="number" step="0.01" value={f.tax_amount} onChange={(e) => set({ tax_amount: e.target.value })} />
                  <div className="flex gap-1 mt-1">{[13, 5, 15].map((r) => <button key={r} type="button" className="text-[10px] px-1.5 py-0.5 rounded bg-[#F3EFE7] text-[#5C6670]" onClick={() => taxFrom(r)}>{r}%</button>)}</div>
                </Field>
                {f.kind === 'expense' ? (
                  <Field label="Paid with"><select className={sel} value={f.payment_method} onChange={(e) => set({ payment_method: e.target.value, reimbursable: f.scope === 'business' && /personal|cash/i.test(e.target.value) ? true : f.reimbursable })}>
                    <option value="" />{(settings?.payment_methods || []).map((m) => <option key={m}>{m}</option>)}</select></Field>
                ) : (
                  <Field label="Due"><Input type="date" value={f.bill.due_date || ''} onChange={(e) => setB({ due_date: e.target.value })} /></Field>
                )}
              </div>
              {f.kind === 'bill' && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 items-end">
                  <Field label="Their invoice no."><Input value={f.bill.number} onChange={(e) => setB({ number: e.target.value })} /></Field>
                  <label className="flex items-center gap-2 text-sm h-10"><Switch checked={f.bill.status === 'paid'} onCheckedChange={(v) => setB({ status: v ? 'paid' : 'unpaid', paid_date: v ? todayISO() : '' })} />Paid</label>
                  {f.bill.status === 'paid' && <Field label="Paid on"><Input type="date" value={f.bill.paid_date || ''} onChange={(e) => setB({ paid_date: e.target.value })} /></Field>}
                </div>
              )}
            </>
          )}

          <Field label={f.scope === 'personal' ? 'Note (optional)' : 'Business reason (for your accountant)'}>
            <Input value={f.reason} onChange={(e) => set({ reason: e.target.value })} placeholder={f.kind === 'mileage' ? 'Meeting with MedWorks about Q4 campaign' : 'Lunch with George re: AMR artist packages'} />
          </Field>

          {f.scope === 'business' && (
            <div className="grid sm:grid-cols-2 gap-2">
              <Field label="Project"><select className={sel} value={f.project_id} onChange={(e) => set({ project_id: e.target.value })}>
                <option value="">No project</option>{projects.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}</select></Field>
              {hasModule(mods, 'invoices') && (
                <Field label="Client (optional)">
                  {f.contact_id ? (
                    <div className="h-10 flex items-center gap-2 text-sm"><span className="truncate">{f.contact_name || 'Linked contact'}</span><button type="button" className="text-[#94A3B8]" onClick={() => set({ contact_id: '', contact_name: '' })}><X className="w-3.5 h-3.5" /></button></div>
                  ) : (
                    <div className="relative">
                      <Input value={contactQ} onChange={(e) => setContactQ(e.target.value)} placeholder="Find a contact…" />
                      {contactHits.length > 0 && (
                        <div className="absolute z-30 left-0 right-0 mt-1 rounded-lg border border-[#E3DDD1] bg-white shadow-lg max-h-48 overflow-y-auto">
                          {contactHits.map((h) => <button key={h.contact_id} type="button" className="w-full text-left px-3 py-1.5 text-sm hover:bg-[#FBF8F2]" onClick={() => { set({ contact_id: h.contact_id, contact_name: h.name || h.company }); setContactQ(''); setContactHits([]); }}>{h.name || h.company}<span className="text-xs text-[#5C6670] ml-1">{h.company}</span></button>)}
                        </div>
                      )}
                    </div>
                  )}
                </Field>
              )}
            </div>
          )}

          {f.scope === 'business' && (
            <div className="grid sm:grid-cols-2 gap-3 pt-1">
              <Toggle k="billable" label="Bill this to the client" hint="It will be waiting under Bill expenses on an invoice" />
              {f.kind !== 'bill' && <Toggle k="reimbursable" label="I paid this myself" hint="Tracked as owed back to you" />}
            </div>
          )}

          {(f.kind === 'expense' || f.kind === 'bill') && (
            <div>
              <span className="text-[11px] text-[#5C6670]">{f.kind === 'bill' ? 'The bill (PDF or photo)' : 'Receipt'}</span>
              <div className="flex gap-2 flex-wrap items-center mt-1">
                <Button type="button" variant="outline" size="sm" onClick={() => camRef.current?.click()}><Camera className="w-4 h-4 mr-1" />Take photo</Button>
                <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()}><Paperclip className="w-4 h-4 mr-1" />Attach file</Button>
                <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { setFiles([...files, ...e.target.files]); e.target.value = ''; }} />
                <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={(e) => { setFiles([...files, ...e.target.files]); e.target.value = ''; }} />
                {(f.receipts || []).map((r) => (
                  <span key={r.id} className="inline-flex items-center gap-1 text-xs bg-[#F3EFE7] rounded-full px-2 py-1">
                    <button type="button" className="underline" onClick={async () => { const x = await expensesAPI.receipt(r.id); openBlob(x.data); }}>{r.filename}</button>
                    <button type="button" onClick={async () => { const x = await expensesAPI.removeReceipt(f.id, r.id); set({ receipts: x.data.receipts }); onSaved(x.data, true); }}><X className="w-3 h-3" /></button>
                  </span>
                ))}
                {files.map((file, i) => (
                  <span key={i} className="inline-flex items-center gap-1 text-xs bg-emerald-50 text-emerald-800 rounded-full px-2 py-1">{file.name}
                    <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))}><X className="w-3 h-3" /></button></span>
                ))}
              </div>
            </div>
          )}

          {f.invoice_number && <p className="text-xs text-[#5C6670]">Billed on invoice {f.invoice_number}.</p>}
          <Button className={`w-full ${red}`} onClick={save} disabled={busy || (priced && calc?.ok === false)}>{busy ? 'Saving…' : editing ? 'Save changes' : `Add ${KIND[f.kind].label.toLowerCase()}`}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ── List ─────────────────────────────────────────────────────────────────────
const title = (e) => (e.kind === 'mileage' ? `${e.mileage?.from || '?'} → ${e.mileage?.to || '?'}` : e.kind === 'per_diem' ? `Meals${e.per_diem?.location ? `, ${e.per_diem.location}` : ''}` : e.vendor || e.category || 'Expense');
const subtitle = (e) => (e.kind === 'mileage' ? `${e.mileage?.distance} km${e.mileage?.round_trip ? ' round trip' : ''} · ${e.mileage?.rate_label}`
  : e.kind === 'per_diem' ? `${e.per_diem?.days} day${e.per_diem?.days === 1 ? '' : 's'} · ${e.per_diem?.meals_label}` : e.category);

const Badge = ({ children, tone = 'muted' }) => {
  const cls = { muted: 'bg-[#F3EFE7] text-[#5C6670]', green: 'bg-emerald-50 text-emerald-800', amber: 'bg-amber-50 text-amber-800', red: 'bg-red-50 text-[#A62B1F]', blue: 'bg-sky-50 text-sky-800' }[tone];
  return <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${cls}`}>{children}</span>;
};

const EntryRow = ({ e, onOpen, admin, onChanged, entity }) => {
  const I = KIND[e.kind]?.icon || Receipt;
  const overdueBill = e.kind === 'bill' && e.bill?.status !== 'paid' && e.bill?.due_date && e.bill.due_date < todayISO();
  return (
    <div className="flex items-start gap-3 p-3 border-t border-[#F0EBE1] hover:bg-[#FBF8F2] cursor-pointer" onClick={() => onOpen(e)}>
      <div className="w-8 h-8 rounded-lg bg-[#F3EFE7] flex items-center justify-center shrink-0"><I className="w-4 h-4 text-[#5C6670]" /></div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-sm font-medium text-[#1E2A33] truncate">{title(e)}</span>
          {entity && <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#5C6670]"><EntityBadge entity={entity} size={14} />{entity.name}</span>}
          {e.scope === 'personal' && <Badge>Personal</Badge>}
          {e.kind === 'bill' && (e.bill?.status === 'paid' ? <Badge tone="green">Paid</Badge> : <Badge tone={overdueBill ? 'red' : 'amber'}>{overdueBill ? 'Overdue' : `Due ${fmtDay(e.bill?.due_date, { month: 'short', day: 'numeric' })}`}</Badge>)}
          {e.billable && (e.invoice_number ? <Badge tone="blue">Billed {e.invoice_number}</Badge> : <Badge tone="amber">To bill</Badge>)}
          {e.reimbursable && (e.reimbursed_at ? <Badge tone="green">Paid back</Badge> : <Badge tone="amber">Owed to {admin && e.user_name ? e.user_name.split(' ')[0] : 'you'}</Badge>)}
          {e.receipts?.length > 0 && <Paperclip className="w-3 h-3 text-[#94A3B8]" />}
        </div>
        <div className="text-xs text-[#5C6670] truncate">{[fmtDay(e.date), subtitle(e), e.project_name, e.reason].filter(Boolean).join(' · ')}</div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-sm font-semibold text-[#1E2A33]">{fmtMoney(e.amount, e.currency)}</div>
        {e.tax_amount > 0 && <div className="text-[11px] text-[#94A3B8]">incl. {fmtMoney(e.tax_amount)} tax</div>}
        {e.kind === 'bill' && e.bill?.status !== 'paid' && (
          <button className="text-[11px] font-semibold text-[#A62B1F] mt-0.5" onClick={async (ev) => { ev.stopPropagation(); await expensesAPI.payBill(e.id); toast.success('Marked paid'); onChanged(); }}>Mark paid</button>
        )}
        {admin && e.reimbursable && !e.reimbursed_at && (
          <button className="text-[11px] font-semibold text-[#A62B1F] mt-0.5 block ml-auto" onClick={async (ev) => { ev.stopPropagation(); await expensesAPI.reimbursed(e.id); toast.success('Marked as paid back'); onChanged(); }}>Paid back</button>
        )}
      </div>
    </div>
  );
};

const KIND_FILTERS = [['', 'Everything'], ['expense', 'Receipts'], ['mileage', 'Mileage'], ['per_diem', 'Per diems'], ['bill', 'Bills']];

const ExpenseList = ({ settings }) => {
  const [period, setPeriod] = useState({ period: 'this_month' });
  const [kind, setKind] = useState('');
  const [scope, setScope] = useState('');
  const [extra, setExtra] = useState('');
  const [projectId, setProjectId] = useState('');
  const [entityId, setEntityId] = useState('');
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [dialog, setDialog] = useState({ open: false, initial: null });
  const { projects = [] } = useProject() || {};
  const admin = isAdmin();

  const load = useCallback(() => {
    const params = { ...periodParams(period), kind: kind || undefined, scope: scope || undefined, project_id: projectId || undefined, q: q || undefined, entity_id: entityId || undefined };
    if (extra === 'owed') params.reimburse = 'owed';
    if (extra === 'unbilled') params.unbilled = true;
    if (extra === 'unpaid') params.bill_status = 'unpaid';
    expensesAPI.list(params).then((r) => setData(r.data)).catch((e) => err(e, 'Could not load expenses'));
  }, [period, kind, scope, projectId, q, extra, entityId]);
  const many = (settings?.entities || []).length > 1;
  useEffect(() => { const t = setTimeout(load, q ? 250 : 0); return () => clearTimeout(t); }, [load, q]);

  const s = data?.summary || {};
  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap">
        {Object.entries(KIND).map(([k, v]) => { const I = v.icon; return <Button key={k} variant={k === 'expense' ? 'default' : 'outline'} className={k === 'expense' ? red : ''} onClick={() => setDialog({ open: true, initial: { kind: k } })}><Plus className="w-4 h-4 mr-0.5" /><I className="w-4 h-4 mr-1" />{v.add}</Button>; })}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Tile label="Business spending" value={fmtMoney(s.business)} note={`incl. ${fmtMoney(s.tax)} HST/GST`} />
        <Tile label="Kilometres" value={`${(s.km || 0).toLocaleString()} km`} note={fmtMoney(s.mileage)} />
        <Tile label="Per diems" value={fmtMoney(s.per_diem)} />
        <Tile label="Owed to you" value={fmtMoney(s.owed_to_you)} note="Paid personally, not yet paid back" />
        <Tile label="Billable, not invoiced" value={fmtMoney(s.unbilled)} />
      </div>

      <div className={`${card} overflow-hidden`}>
        <div className="p-3 space-y-2 border-b border-[#E3DDD1]">
          <div className="flex items-center gap-2 flex-wrap">
            <PeriodPicker value={period} onChange={setPeriod} />
            <select className="h-9 rounded-md border border-input px-2 text-sm bg-white" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
              <option value="">All projects</option><option value="none">No project</option>{projects.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
            </select>
            <select className="h-9 rounded-md border border-input px-2 text-sm bg-white" value={scope} onChange={(e) => setScope(e.target.value)}>
              <option value="">Business and personal</option><option value="business">Business only</option><option value="personal">Personal only</option>
            </select>
            {many && (
              <select className="h-9 rounded-md border border-input px-2 text-sm bg-white" value={entityId} onChange={(e) => setEntityId(e.target.value)}>
                <option value="">Everyone</option>{settings.entities.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
              </select>
            )}
            <div className="relative ml-auto w-full sm:w-56">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-[#94A3B8]" />
              <Input className="pl-8 h-9" placeholder="Search" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
          </div>
          <div className="flex gap-1 flex-wrap">
            {KIND_FILTERS.map(([k, l]) => <Chip key={k} on={kind === k && !extra} onClick={() => { setKind(k); setExtra(''); }}>{l}</Chip>)}
            <span className="w-px bg-[#E3DDD1] mx-1" />
            <Chip on={extra === 'owed'} onClick={() => setExtra(extra === 'owed' ? '' : 'owed')}>Owed to you</Chip>
            <Chip on={extra === 'unbilled'} onClick={() => setExtra(extra === 'unbilled' ? '' : 'unbilled')}>To bill</Chip>
            <Chip on={extra === 'unpaid'} onClick={() => setExtra(extra === 'unpaid' ? '' : 'unpaid')}>Unpaid bills</Chip>
          </div>
        </div>
        {!data ? <div className="p-6 text-sm text-[#5C6670]">Loading…</div> : data.expenses.length === 0 ? (
          <div className="p-10 text-center text-sm text-[#5C6670]"><Receipt className="w-8 h-8 mx-auto text-[#C9C0B0] mb-2" />Nothing here for this period.</div>
        ) : data.expenses.map((e) => <EntryRow key={e.id} e={e} entity={many && !entityId ? entityById(settings, e.entity_id) : null} admin={admin} onChanged={load} onOpen={(x) => setDialog({ open: true, initial: x })} />)}
      </div>

      <ExpenseDialog open={dialog.open} onOpenChange={(o) => setDialog({ ...dialog, open: o })} initial={dialog.initial} settings={settings}
        onSaved={(x, keepOpen) => { load(); if (keepOpen) setDialog({ open: true, initial: x }); }} />
    </div>
  );
};

// ── Report ───────────────────────────────────────────────────────────────────
const Report = ({ settings }) => {
  const [period, setPeriod] = useState({ period: 'last_quarter' });
  const [personal, setPersonal] = useState(false);
  const [projectId, setProjectId] = useState('');
  const [entityId, setEntityId] = useState('');
  const [r, setR] = useState(null);
  const { projects = [] } = useProject() || {};
  const params = { ...periodParams(period), include_personal: personal, project_id: projectId || undefined, entity_id: entityId || undefined };
  useEffect(() => { expensesAPI.report(params).then((x) => setR(x.data)).catch((e) => err(e, 'Could not build the report')); }, [JSON.stringify(params)]); // eslint-disable-line react-hooks/exhaustive-deps
  const dl = async (kind, name) => {
    try { const x = await expensesAPI.download(kind, params); openBlob(x.data, kind === 'report.pdf' ? null : name); } catch (e) { err(e, 'Could not download'); }
  };
  const t = r?.totals || {};
  const slug = `${r?.from || 'all'}-to-${r?.to || 'now'}`;
  return (
    <div className="space-y-4">
      <div className={`${card} p-4 flex items-center gap-3 flex-wrap`}>
        <PeriodPicker value={period} onChange={setPeriod} />
        <select className="h-9 rounded-md border border-input px-2 text-sm bg-white" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
          <option value="">All projects</option>{projects.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
        </select>
        {(settings?.entities || []).length > 1 && (
          <select className="h-9 rounded-md border border-input px-2 text-sm bg-white" value={entityId} onChange={(e) => setEntityId(e.target.value)}>
            <option value="">Everyone</option>{settings.entities.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select>
        )}
        <label className="flex items-center gap-2 text-sm"><Switch checked={personal} onCheckedChange={setPersonal} />Include personal</label>
        <div className="ml-auto flex gap-2 flex-wrap">
          <Button className={red} onClick={() => dl('export.zip', `expenses-${slug}.zip`)}><Archive className="w-4 h-4 mr-1" />Accountant pack</Button>
          <Button variant="outline" onClick={() => dl('report.pdf')}><FileDown className="w-4 h-4 mr-1" />PDF</Button>
          <Button variant="outline" onClick={() => dl('export.csv', `expenses-${slug}.csv`)}><Download className="w-4 h-4 mr-1" />CSV</Button>
        </div>
      </div>
      <p className="text-xs text-[#5C6670] flex items-center gap-1"><Info className="w-3.5 h-3.5" />The accountant pack is one ZIP: the report PDF, a spreadsheet of every entry and all the receipts, named by date and vendor. {r?.label}</p>
      {!r ? <div className={`${card} p-6 text-sm text-[#5C6670]`}>Loading…</div> : (
        <>
          <div className={`${card} p-4 flex items-center gap-3`}>
            <EntityBadge entity={r.header} size={40} />
            <div className="min-w-0">
              <div className="text-lg font-semibold text-[#1E2A33]" style={serif}>{r.header?.name}: expense report</div>
              <div className="text-xs text-[#5C6670]">{[r.header?.all ? 'Everyone' : r.header?.type === 'individual' ? 'Individual' : 'Company', r.label, r.header?.tax_number].filter(Boolean).join(' · ')}</div>
            </div>
            <span className="ml-auto text-[11px] text-[#94A3B8] hidden sm:block">This heads the PDF and accountant pack</span>
          </div>
          {r.by_entity?.length > 1 && (
            <div className="flex gap-2 flex-wrap">
              {r.by_entity.map((x) => (
                <button key={x.entity_id || 'none'} onClick={() => x.entity_id && setEntityId(x.entity_id)} className={`${card} px-3 py-2 flex items-center gap-2 text-sm hover:border-[#1E2A33]`}>
                  <EntityBadge entity={entityById(settings, x.entity_id) || { name: x.entity, type: x.type }} size={20} />{x.entity}<span className="font-semibold">{fmtMoney(x.amount)}</span>
                </button>
              ))}
            </div>
          )}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Tile label="Business total" value={fmtMoney(t.business)} note={`${t.count} entries`} />
            <Tile label="HST/GST paid" value={fmtMoney(t.tax)} note="Possible input tax credits" />
            <Tile label="Mileage" value={fmtMoney(t.mileage)} note={`${(t.km || 0).toLocaleString()} km`} />
            <Tile label="Per diems" value={fmtMoney(t.per_diem)} />
            {personal ? <Tile label="Personal" value={fmtMoney(t.personal)} /> : <Tile label="Owed to you" value={fmtMoney(t.owed_to_you)} />}
          </div>
          <div className="grid lg:grid-cols-2 gap-4">
            <div className={`${card} overflow-hidden`}>
              <div className="px-4 py-3 font-semibold text-[#1E2A33] border-b border-[#E3DDD1]">By category</div>
              {r.by_category.length === 0 ? <div className="p-4 text-sm text-[#5C6670]">Nothing in this period.</div> : (
                <table className="w-full text-sm"><tbody>
                  {r.by_category.map((c) => <tr key={c.category} className="border-t border-[#F0EBE1]"><td className="p-2.5 pl-4">{c.category}<span className="text-xs text-[#94A3B8] ml-1">{c.count}</span></td><td className="p-2.5 text-right text-[#5C6670]">{c.tax ? fmtMoney(c.tax) : ''}</td><td className="p-2.5 pr-4 text-right font-medium">{fmtMoney(c.amount)}</td></tr>)}
                </tbody></table>
              )}
            </div>
            <div className="space-y-4">
              <div className={`${card} overflow-hidden`}>
                <div className="px-4 py-3 font-semibold text-[#1E2A33] border-b border-[#E3DDD1]">By project</div>
                <table className="w-full text-sm"><tbody>
                  {r.by_project.map((p) => <tr key={p.project} className="border-t border-[#F0EBE1]"><td className="p-2.5 pl-4">{p.project}</td><td className="p-2.5 text-right text-xs text-[#5C6670]">{p.billable ? `${fmtMoney(p.billable)} billable` : ''}</td><td className="p-2.5 pr-4 text-right font-medium">{fmtMoney(p.amount)}</td></tr>)}
                </tbody></table>
              </div>
              <div className={`${card} p-4`}>
                <div className="font-semibold text-[#1E2A33] mb-2">By month</div>
                {(() => { const max = Math.max(1, ...r.by_month.map((m) => m.amount)); return r.by_month.map((m) => (
                  <div key={m.month} className="flex items-center gap-2 text-sm py-0.5">
                    <span className="w-16 text-[#5C6670]">{fmtDay(`${m.month}-01`, { month: 'short', year: '2-digit' })}</span>
                    <div className="flex-1 h-2 rounded-full bg-[#F3EFE7] overflow-hidden"><div className="h-full bg-[#A62B1F]" style={{ width: `${(m.amount / max) * 100}%` }} /></div>
                    <span className="w-24 text-right">{fmtMoney(m.amount)}</span>
                  </div>)); })()}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ── Rates ────────────────────────────────────────────────────────────────────
const quarterStart = (offset = 1) => {
  const d = new Date(); const q = Math.floor(d.getMonth() / 3) + offset;
  const dt = new Date(d.getFullYear(), q * 3, 1);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-01`;
};

const RateDialog = ({ open, onOpenChange, initial, onSaved }) => {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(initial || {}); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const num = (k, label, step = '0.01', suffix) => (
    <Field label={label}><div className="flex items-center gap-1"><Input type="number" step={step} value={f[k] ?? ''} onChange={(e) => setF({ ...f, [k]: e.target.value })} />{suffix && <span className="text-xs text-[#5C6670]">{suffix}</span>}</div></Field>
  );
  const save = async () => {
    setBusy(true);
    const n = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
    const body = { region: f.region || 'Canada', effective_from: f.effective_from, source: f.source || '',
      km_rate: n(f.km_rate), km_threshold: n(f.km_threshold), km_rate_after: n(f.km_rate_after),
      breakfast: n(f.breakfast), lunch: n(f.lunch), dinner: n(f.dinner), incidentals: n(f.incidentals) };
    try {
      const r = f.id ? await expensesAPI.updateRate(f.id, body) : await expensesAPI.addRate(body);
      onOpenChange(false); onSaved(r.data);
    } catch (e) { err(e, 'Could not save the rate'); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{f.id ? 'Edit rate' : 'New rate'}</DialogTitle>
          <DialogDescription>Applies from its start date until the next rate for the same region. Leave a box blank if it doesn't apply.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <Field label="Region"><Input value={f.region || ''} onChange={(e) => setF({ ...f, region: e.target.value })} placeholder="Canada, Ontario…" /></Field>
            <Field label="Starts"><Input type="date" value={f.effective_from || ''} onChange={(e) => setF({ ...f, effective_from: e.target.value })} /></Field>
          </div>
          <div className="text-xs font-semibold uppercase tracking-wider text-[#5C6670] pt-1">Mileage</div>
          <div className="grid grid-cols-3 gap-2">
            {num('km_rate', 'Per km ($)', '0.001')}
            {num('km_threshold', 'Up to (km/yr)', '1')}
            {num('km_rate_after', 'Then per km ($)', '0.001')}
          </div>
          <div className="text-xs font-semibold uppercase tracking-wider text-[#5C6670] pt-1">Meal allowances (per day)</div>
          <div className="grid grid-cols-4 gap-2">{num('breakfast', 'Breakfast')}{num('lunch', 'Lunch')}{num('dinner', 'Dinner')}{num('incidentals', 'Incidentals')}</div>
          <Field label="Source (where the rate came from)"><Textarea rows={2} value={f.source || ''} onChange={(e) => setF({ ...f, source: e.target.value })} placeholder="e.g. NJC Appendix B, Ontario, Oct 1 2026" /></Field>
          <Button className={`w-full ${red}`} onClick={save} disabled={busy || !f.effective_from}>{busy ? 'Saving…' : 'Save rate'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Rates = ({ onChanged }) => {
  const [rows, setRows] = useState(null);
  const [dialog, setDialog] = useState({ open: false, initial: null });
  const [apply, setApply] = useState(null);
  const admin = isAdmin();
  const load = () => expensesAPI.rates().then((r) => setRows(r.data.rates)).catch((e) => err(e, 'Could not load rates'));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const c = (v) => (v == null ? '–' : v < 1 ? `${Math.round(v * 1000) / 10}¢` : fmtMoney(v));
  const newFrom = (r) => { const { id, current, future, created_at, created_by, seeded, updated_at, updated_by, company_id, ...rest } = r || {}; return { ...rest, effective_from: quarterStart(1), source: '' }; };
  const regions = [...new Set((rows || []).map((r) => r.region))];
  return (
    <div className="space-y-4">
      <div className={`${card} p-4 text-sm text-[#33404A] space-y-1`}>
        <p><strong>How rates work.</strong> Each rate applies from its start date until the next one for the same region, so when a new quarter's rates come out you add a new row and older trips keep the rate they were priced at.</p>
        <p className="text-xs text-[#5C6670]">The starting rates are the CRA 2026 reasonable per-km allowance (73¢ for the first 5,000 km, 67¢ after) and the NJC meal allowances from April 1, 2026. Your accountant may prefer different figures: change them to suit.</p>
      </div>
      {admin && <Button className={red} onClick={() => setDialog({ open: true, initial: newFrom(rows?.find((r) => r.current)) })}><Plus className="w-4 h-4 mr-1" />Add the next rate</Button>}
      {!rows ? <div className={`${card} p-6 text-sm text-[#5C6670]`}>Loading…</div> : regions.map((reg) => (
        <div key={reg} className={`${card} overflow-x-auto`}>
          <div className="px-4 py-3 font-semibold text-[#1E2A33] border-b border-[#E3DDD1]">{reg}</div>
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-[#5C6670] bg-[#FBF8F2]">
              <tr><th className="text-left p-2 pl-4">From</th><th className="text-right p-2">Per km</th><th className="text-right p-2">Threshold</th><th className="text-right p-2">After</th>
                <th className="text-right p-2">Bkfst</th><th className="text-right p-2">Lunch</th><th className="text-right p-2">Dinner</th><th className="text-right p-2">Incid.</th><th className="text-left p-2">Source</th><th className="p-2" /></tr>
            </thead>
            <tbody>
              {rows.filter((r) => r.region === reg).map((r) => (
                <tr key={r.id} className={`border-t border-[#F0EBE1] ${r.current ? 'bg-emerald-50/40' : ''}`}>
                  <td className="p-2 pl-4 whitespace-nowrap">{fmtDay(r.effective_from)} {r.current && <Badge tone="green">Current</Badge>} {r.future && <Badge tone="blue">Upcoming</Badge>}</td>
                  <td className="p-2 text-right">{c(r.km_rate)}</td><td className="p-2 text-right">{r.km_threshold ? `${Number(r.km_threshold).toLocaleString()} km` : '–'}</td><td className="p-2 text-right">{r.km_threshold ? c(r.km_rate_after) : '–'}</td>
                  <td className="p-2 text-right">{c(r.breakfast)}</td><td className="p-2 text-right">{c(r.lunch)}</td><td className="p-2 text-right">{c(r.dinner)}</td><td className="p-2 text-right">{c(r.incidentals)}</td>
                  <td className="p-2 text-xs text-[#5C6670] max-w-xs">{r.source}</td>
                  <td className="p-2 whitespace-nowrap text-right">{admin && (<>
                    <button className="p-1 text-[#5C6670]" title="Edit" onClick={() => setDialog({ open: true, initial: r })}><Pencil className="w-3.5 h-3.5" /></button>
                    <button className="p-1 text-[#5C6670]" title="Start the next rate from this one" onClick={() => setDialog({ open: true, initial: newFrom(r) })}><Plus className="w-3.5 h-3.5" /></button>
                    <button className="p-1 text-[#A62B1F]" title="Delete" onClick={async () => { if (!window.confirm('Delete this rate? Entries already priced keep their amounts.')) return; await expensesAPI.removeRate(r.id); load(); }}><Trash2 className="w-3.5 h-3.5" /></button>
                  </>)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
      <RateDialog open={dialog.open} onOpenChange={(o) => setDialog({ ...dialog, open: o })} initial={dialog.initial}
        onSaved={(r) => { load(); onChanged(); toast.success('Rate saved'); if (r.affected?.priced_differently - r.affected?.locked > 0) setApply(r); }} />
      <Dialog open={!!apply} onOpenChange={(o) => !o && setApply(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Re-price earlier entries?</DialogTitle>
            <DialogDescription>{apply && `${apply.affected.priced_differently - apply.affected.locked} mileage or per diem entr${apply.affected.priced_differently - apply.affected.locked === 1 ? 'y' : 'ies'} from ${fmtDay(apply.effective_from)} on ${apply.affected.priced_differently - apply.affected.locked === 1 ? 'was' : 'were'} priced at an older rate.`}
              {apply?.affected.locked > 0 && ` ${apply.affected.locked} already invoiced or paid back will be left alone.`}</DialogDescription></DialogHeader>
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={() => setApply(null)}>Leave them</Button>
            <Button className={`flex-1 ${red}`} onClick={async () => { const r = await expensesAPI.applyRate(apply.id); toast.success(`${r.data.changed} entr${r.data.changed === 1 ? 'y' : 'ies'} re-priced`); setApply(null); onChanged(); }}>Use the new rate</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ── Settings ─────────────────────────────────────────────────────────────────
const EntityCard = ({ entity, isDefault, onSaved }) => {
  const [f, setF] = useState(entity);
  const [busy, setBusy] = useState(false);
  const logoRef = useRef(null);
  useEffect(() => { setF(entity); }, [entity]);
  const dirty = ['name', 'type', 'address', 'tax_number'].some((k) => (f[k] || '') !== (entity[k] || ''));
  const run = async (fn, msg) => { setBusy(true); try { const r = await fn(); onSaved(r.data); if (msg) toast.success(msg); } catch (e) { err(e, 'Could not save'); } finally { setBusy(false); } };
  return (
    <div className="rounded-lg border border-[#E3DDD1] p-3 space-y-2">
      <div className="flex items-center gap-3">
        <button type="button" title="Upload a logo" onClick={() => logoRef.current?.click()} className="shrink-0 rounded-lg border border-dashed border-[#C9C0B0] p-1.5 hover:border-[#1E2A33]">
          {entity.logo_url ? <img src={entity.logo_url} alt="" className="h-10 max-w-[120px] object-contain" /> : <span className="flex flex-col items-center"><EntityBadge entity={entity} size={32} /><span className="text-[9px] text-[#5C6670] mt-0.5">Add logo</span></span>}
        </button>
        <input ref={logoRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" className="hidden"
          onChange={(e) => { const file = e.target.files?.[0]; e.target.value = ''; if (file) run(() => expensesAPI.uploadEntityLogo(entity.id, file), 'Logo updated'); }} />
        <div className="flex-1 grid sm:grid-cols-[1fr_130px] gap-2">
          <Input value={f.name || ''} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Name" />
          <select className={sel} value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}><option value="company">Company</option><option value="individual">Individual</option></select>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        <Textarea rows={2} value={f.address || ''} onChange={(e) => setF({ ...f, address: e.target.value })} placeholder="Address (shown on the report)" />
        <Textarea rows={2} value={f.tax_number || ''} onChange={(e) => setF({ ...f, tax_number: e.target.value })} placeholder={f.type === 'individual' ? 'Optional note, e.g. sole proprietor' : 'Business / HST number'} />
      </div>
      <div className="flex items-center gap-3 flex-wrap text-xs">
        {isDefault ? <span className="font-semibold text-emerald-800">Default for new expenses</span>
          : <button className="font-semibold text-[#A62B1F]" disabled={busy} onClick={() => run(() => expensesAPI.updateSettings({ default_entity_id: entity.id }), 'Default changed')}>Make default</button>}
        {entity.logo_url && <button className="text-[#5C6670]" disabled={busy} onClick={() => run(() => expensesAPI.updateEntity(entity.id, { logo_url: '' }), 'Logo removed')}>Remove logo</button>}
        <button className="text-[#5C6670] hover:text-[#A62B1F]" disabled={busy} onClick={() => { if (window.confirm(`Remove ${entity.name}?`)) run(() => expensesAPI.removeEntity(entity.id), 'Removed'); }}>Remove</button>
        {dirty && <Button size="sm" className={`ml-auto ${red}`} disabled={busy} onClick={() => run(() => expensesAPI.updateEntity(entity.id, { name: f.name, type: f.type, address: f.address || '', tax_number: f.tax_number || '' }), 'Saved')}>Save</Button>}
      </div>
    </div>
  );
};

const EntitySettings = ({ settings, onSaved }) => (
  <div className={`${card} p-4 space-y-3 max-w-3xl`}>
    <div>
      <h3 className="font-semibold text-[#1E2A33]">Individuals and companies</h3>
      <p className="text-xs text-[#5C6670]">Who expenses are recorded for: the company, another business you run, or a person (you as a sole proprietor, a team member). Each gets its own logo, which heads its report and accountant pack. Click the logo box to upload one.</p>
    </div>
    {(settings?.entities || []).map((x) => <EntityCard key={x.id} entity={x} isDefault={settings.default_entity_id === x.id} onSaved={onSaved} />)}
    <div className="flex gap-2">
      <Button variant="outline" size="sm" onClick={async () => { try { const r = await expensesAPI.addEntity({ type: 'company', name: 'New company' }); onSaved(r.data); } catch (e) { err(e, 'Could not add'); } }}><Plus className="w-4 h-4 mr-1" />Company</Button>
      <Button variant="outline" size="sm" onClick={async () => { try { const r = await expensesAPI.addEntity({ type: 'individual', name: 'New person' }); onSaved(r.data); } catch (e) { err(e, 'Could not add'); } }}><Plus className="w-4 h-4 mr-1" />Individual</Button>
    </div>
  </div>
);

const ExpenseSettings = ({ settings, onSaved }) => {
  const [f, setF] = useState({ categories: '', payment_methods: '', default_region: '' });
  useEffect(() => { if (settings) setF({ categories: settings.categories.join('\n'), payment_methods: settings.payment_methods.join('\n'), default_region: settings.default_region }); }, [settings]);
  return (
    <div className={`${card} p-4 space-y-3 max-w-3xl`}>
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Categories (one per line)"><Textarea rows={14} value={f.categories} onChange={(e) => setF({ ...f, categories: e.target.value })} /></Field>
        <div className="space-y-3">
          <Field label="Ways of paying (one per line)"><Textarea rows={7} value={f.payment_methods} onChange={(e) => setF({ ...f, payment_methods: e.target.value })} /></Field>
          <Field label="Default rate region"><select className={sel} value={f.default_region} onChange={(e) => setF({ ...f, default_region: e.target.value })}>{(settings?.regions || []).map((r) => <option key={r}>{r}</option>)}</select></Field>
          <p className="text-xs text-[#5C6670]">Categories follow the CRA business-expense headings so your accountant can map them straight across.</p>
        </div>
      </div>
      <Button className={red} onClick={async () => {
        try { const r = await expensesAPI.updateSettings({ categories: f.categories.split('\n'), payment_methods: f.payment_methods.split('\n'), default_region: f.default_region }); onSaved(r.data); toast.success('Saved'); } catch (e) { err(e, 'Could not save'); }
      }}>Save</Button>
    </div>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────
const TABS = [['', 'Expenses'], ['report', 'Report'], ['rates', 'Rates'], ['settings', 'Settings']];

export default function Expenses() {
  const { tab = '' } = useParams();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [version, setVersion] = useState(0);
  const loadSettings = () => expensesAPI.settings().then((r) => setSettings(r.data)).catch(() => {});
  useEffect(() => { loadSettings(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const tabs = TABS.filter(([k]) => k !== 'settings' || isAdmin());
  return (
    <Layout title="Expenses">
      <div className="space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-semibold text-[#1E2A33] mr-auto" style={serif}>Expenses</h1>
          <div className="flex gap-1 bg-[#F3EFE7] rounded-lg p-1">
            {tabs.map(([k, l]) => <button key={k} onClick={() => navigate(k ? `/expenses/${k}` : '/expenses')} className={`text-sm px-3 py-1.5 rounded-md ${tab === k ? 'bg-white shadow-sm text-[#1E2A33] font-semibold' : 'text-[#5C6670]'}`}>{l}</button>)}
          </div>
        </div>
        {tab === 'report' ? <Report settings={settings} /> : tab === 'rates' ? <Rates onChanged={() => { loadSettings(); setVersion(version + 1); }} />
          : tab === 'settings' ? <div className="space-y-4"><EntitySettings settings={settings} onSaved={setSettings} /><ExpenseSettings settings={settings} onSaved={setSettings} /></div> : <ExpenseList key={version} settings={settings} />}
      </div>
    </Layout>
  );
}
